import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { roleHome } from "@/lib/roles";
import { Loader2, Building2, Factory, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GSTIN_RX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const EMAIL_RX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const EMPTY = {
  full_name: "", email: "", phone: "", designation: "", password: "", confirm_password: "",
  company_name: "", gst_number: "", company_email: "", address: "", city: "", state: "", country: "India", seller_code: "",
};

function Field({ id, label, field, type = "text", required = true, placeholder = "", mono = false, maxLength, value, error, onChange }) {
  return (
    <div>
      <Label htmlFor={id}>{label}{required && " *"}</Label>
      <Input id={id} data-testid={`register-${field}`} type={type} value={value} maxLength={maxLength}
        onChange={(e) => onChange(field, field === "gst_number" ? e.target.value.toUpperCase() : e.target.value)}
        placeholder={placeholder} className={`mt-1.5 ${mono ? "font-mono" : ""} ${error ? "border-red-400" : ""}`} />
      {error && <p data-testid={`register-error-${field}`} className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default function RegisterForm({ role }) {
  const isEnterprise = role === "enterprise";
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Full name is required";
    if (!EMAIL_RX.test(form.email.trim())) e.email = "Enter a valid work email";
    if (!form.phone.trim()) e.phone = "Phone number is required";
    if (!form.designation.trim()) e.designation = "Designation is required";
    if (form.password.length < 8) e.password = "Minimum 8 characters";
    if (form.password !== form.confirm_password) e.confirm_password = "Passwords do not match";
    if (!form.company_name.trim()) e.company_name = "Company name is required";
    if (!GSTIN_RX.test(form.gst_number.toUpperCase().trim())) e.gst_number = "Enter a valid 15-character GST number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const r = await api.post(`/auth/register/${role}`, form);
      setSuccess(r.data.message);
      if (role !== "enterprise") {
        setUser(r.data.user);
        setTimeout(() => navigate(roleHome(r.data.user.role)), 900);
      }
    } catch (err) {
      setServerError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen md:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-slate-900 md:block">
        <img src="https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzdXBwbHklMjBjaGFpbiUyMHNoaXBwaW5nJTIwY2FyZ298ZW58MHx8fHwxNzg2MzcxMzEyfDA&ixlib=rb-4.1.0&q=85"
          alt="Global logistics" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="relative flex h-full flex-col justify-between p-10 text-white">
          <Link to="/" className="flex items-center gap-2"><span className="rounded-lg bg-white px-3 py-1.5"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-7 w-auto" /></span></Link>
          <div>
            {isEnterprise
              ? <><h2 className="font-heading text-4xl font-semibold leading-tight">Set up your procurement workspace.</h2>
                  <ul className="mt-5 max-w-md space-y-1.5 text-slate-300">
                    {["Invite your buyers.", "Connect with suppliers.", "Run sourcing events.", "Compare offers.", "Manage approvals.", "Track purchase orders."].map((t) => (
                      <li key={t} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-blue-400" />{t}</li>
                    ))}
                  </ul></>
              : <><h2 className="font-heading text-4xl font-semibold leading-tight">One workspace for every customer PO.</h2>
                  <p className="mt-4 max-w-md text-slate-300">Respond to procurement opportunities, manage quotations and collaborate with your buyer customers from one platform.</p></>}
          </div>
          <p className="text-xs text-slate-400">ProcDeal · Procurement Management Platform</p>
        </div>
      </div>

      <div className="flex items-center justify-center overflow-y-auto bg-background p-6">
        <div className="w-full max-w-lg py-8">
          <Link to="/" className="mb-6 flex items-center gap-2 md:hidden"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" /></Link>
          <div className="flex items-center gap-2">
            {isEnterprise ? <Building2 className="h-6 w-6" /> : <Factory className="h-6 w-6" />}
            <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="register-title">
              {isEnterprise ? "Create your Enterprise account" : "Join ProcDeal as a Supplier"}
            </h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {isEnterprise
              ? "Bring your procurement team onto ProcDeal."
              : "Respond to procurement opportunities, manage quotations and collaborate with your buyer customers from one platform."}
          </p>

          {success ? (
            <div data-testid="register-success" className="mt-8 rounded-lg border border-green-200 bg-green-50 p-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#22C55E]" />
              <p className="mt-3 font-heading text-lg font-semibold text-green-900">{success}</p>
              {isEnterprise ? (
                <>
                  <p className="mt-1 text-sm text-green-700">Sign in with your email and password to open your Enterprise Dashboard.</p>
                  <Button data-testid="register-goto-login" className="mt-4 bg-slate-900 hover:bg-slate-800"
                    onClick={() => navigate("/login")}>Continue to Sign In</Button>
                </>
              ) : (
                <>
                  <p className="mt-1 text-sm text-green-700">Taking you to your dashboard…</p>
                  <Button data-testid="register-goto-dashboard" className="mt-4 bg-slate-900 hover:bg-slate-800"
                    onClick={() => navigate(roleHome("supplier"))}>Go to Seller Dashboard</Button>
                </>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-6" noValidate={false}>
              <section>
                <h2 className="border-b pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Personal Information</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field id="fn" label="Full Name" field="full_name" placeholder="Rahul Sharma" value={form.full_name} error={errors.full_name} onChange={set} />
                  <Field id="em" label="Work Email" field="email" type="email" placeholder="you@company.com" value={form.email} error={errors.email} onChange={set} />
                  <Field id="ph" label="Phone Number" field="phone" placeholder="+91 98XXX XXXXX" value={form.phone} error={errors.phone} onChange={set} />
                  <Field id="dg" label="Designation" field="designation" placeholder={isEnterprise ? "Procurement Manager" : "Sales Manager"} value={form.designation} error={errors.designation} onChange={set} />
                  <Field id="pw" label="Password" field="password" type="password" placeholder="Min. 8 characters" value={form.password} error={errors.password} onChange={set} />
                  <Field id="cpw" label="Confirm Password" field="confirm_password" type="password" placeholder="Repeat password" value={form.confirm_password} error={errors.confirm_password} onChange={set} />
                </div>
              </section>

              <section>
                <h2 className="border-b pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Company Information</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Field id="cn" label={isEnterprise ? "Company / Organization Name" : "Seller / Company Name"} field="company_name" placeholder={isEnterprise ? "Acme Industries Ltd" : "ABC Manufacturing Pvt Ltd"} value={form.company_name} error={errors.company_name} onChange={set} />
                  </div>
                  <Field id="gst" label="GST Number" field="gst_number" placeholder="27ABCDE1234F1Z5" mono maxLength={15} value={form.gst_number} error={errors.gst_number} onChange={set} />
                  <Field id="ce" label="Company Email" field="company_email" type="email" required={false} placeholder="info@company.com" />
                  <div className="sm:col-span-2"><Field id="ad" label="Company Address" field="address" required={false} placeholder="Plot 12, MIDC Industrial Area" value={form.address} error={errors.address} onChange={set} /></div>
                  <Field id="ct" label="City" field="city" required={false} placeholder="Pune" value={form.city} error={errors.city} onChange={set} />
                  <Field id="st" label="State" field="state" required={false} placeholder="Maharashtra" value={form.state} error={errors.state} onChange={set} />
                  <Field id="co" label="Country" field="country" required={false} value={form.country} error={errors.country} onChange={set} />
                  {!isEnterprise && <Field id="sc" label="Seller Code (optional)" field="seller_code" required={false} placeholder="SUP-001" mono value={form.seller_code} error={errors.seller_code} onChange={set} />}
                </div>
              </section>

              {serverError && <p data-testid="register-server-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{serverError}</p>}

              <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEnterprise ? "Create Enterprise Account" : "Register as Seller"}
              </Button>
              <p className="text-center text-sm text-muted-foreground">
                Already have an account? <Link to="/login" data-testid="register-signin-link" className="font-medium text-blue-700 hover:underline">Sign in</Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
